/**
 * Push Notification Types
 * Types for browser Push Notifications API integration
 */

/**
 * Push notification permission state
 */
export type PushPermissionState = 'default' | 'granted' | 'denied';

/**
 * Push subscription data sent to server
 * Mirrors the PushSubscription.toJSON() format
 */
export interface PushSubscriptionData {
  endpoint: string;
  expirationTime: number | null;
  keys: {
    p256dh: string;
    auth: string;
  };
}

/**
 * Server-stored push subscription record
 */
export interface PushSubscriptionRecord {
  id: string;
  userId: string;
  endpoint: string;
  deviceName: string;
  browser: string;
  platform: string;
  createdAt: string;
  lastUsedAt: string;
  isActive: boolean;
}

/**
 * Push notification action button
 */
export interface PushNotificationAction {
  action: string;
  title: string;
  icon?: string;
}

/**
 * Push notification payload from server
 */
export interface PushNotificationPayload {
  title: string;
  body: string;
  icon?: string;
  badge?: string;
  image?: string;
  tag?: string;
  requireInteraction?: boolean;
  silent?: boolean;
  timestamp?: number;
  vibrate?: number[];
  data?: {
    url?: string;
    notificationId?: string;
    type?: string;
    [key: string]: unknown;
  };
  actions?: PushNotificationAction[];
}

/**
 * Request to subscribe to push notifications
 */
export interface PushSubscribeRequest {
  subscription: PushSubscriptionData;
  deviceName?: string;
  userAgent: string;
  timestamp: string;
}

/**
 * Request to unsubscribe from push notifications
 */
export interface PushUnsubscribeRequest {
  endpoint: string;
}

/**
 * Response from VAPID public key endpoint
 */
export interface VapidPublicKeyResponse {
  publicKey: string;
}

/**
 * Response from subscription endpoints
 */
export interface PushSubscriptionResponse {
  success: boolean;
  message: string;
  subscription?: PushSubscriptionRecord;
}

/**
 * Response from get subscriptions endpoint
 */
export interface PushSubscriptionsListResponse {
  subscriptions: PushSubscriptionRecord[];
  count: number;
}

/**
 * Test notification request
 */
export interface TestNotificationRequest {
  title?: string;
  body?: string;
  url?: string;
}

/**
 * Push notification state for hooks/components
 */
export interface PushNotificationState {
  isSupported: boolean;
  permission: PushPermissionState;
  isSubscribed: boolean;
  subscription: PushSubscription | null;
  subscriptions: PushSubscriptionRecord[];
  loading: boolean;
  error: string | null;
}

/**
 * Push notification hook return type
 */
export interface UsePushNotificationsReturn extends PushNotificationState {
  requestPermission: () => Promise<boolean>;
  subscribe: (deviceName?: string) => Promise<boolean>;
  unsubscribe: () => Promise<boolean>;
  removeDevice: (endpoint: string) => Promise<boolean>;
  sendTestNotification: () => Promise<boolean>;
  refreshSubscriptions: () => Promise<void>;
  clearError: () => void;
}

/**
 * Device info for subscription
 */
export interface DeviceInfo {
  name: string;
  browser: string;
  platform: string;
  isMobile: boolean;
}

/**
 * Parse user agent to get device info
 */
export function getDeviceInfo(): DeviceInfo {
  const ua = navigator.userAgent;
  let browser = 'Unknown';
  let platform = 'Unknown';
  const isMobile = /Mobile|Android|iPhone|iPad/.test(ua);

  // Detect browser
  if (ua.includes('Chrome') && !ua.includes('Edg')) {
    browser = 'Chrome';
  } else if (ua.includes('Safari') && !ua.includes('Chrome')) {
    browser = 'Safari';
  } else if (ua.includes('Firefox')) {
    browser = 'Firefox';
  } else if (ua.includes('Edg')) {
    browser = 'Edge';
  } else if (ua.includes('Opera') || ua.includes('OPR')) {
    browser = 'Opera';
  }

  // Detect platform
  if (ua.includes('Windows')) {
    platform = 'Windows';
  } else if (ua.includes('Mac')) {
    platform = 'macOS';
  } else if (ua.includes('Linux')) {
    platform = 'Linux';
  } else if (ua.includes('Android')) {
    platform = 'Android';
  } else if (ua.includes('iPhone') || ua.includes('iPad')) {
    platform = 'iOS';
  }

  // Generate device name
  const deviceType = isMobile ? 'Mobile' : 'Desktop';
  const name = `${browser} on ${platform} (${deviceType})`;

  return {
    name,
    browser,
    platform,
    isMobile,
  };
}
